/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChangeEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import MultiSelectInputBox from '../components/MultiSelectInputBox';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import {
  ActionData,
  AddDeviceV2Props,
  DeviceType,
  DeviceAttributes,
  Group
} from '../constants/telosair-interfaces';
import { addDeviceV2, editDevice } from '../services/dashboard-service';
import {
  deleteDevicePhoto,
  removeDeviceImage,
  resolveDeviceImage,
  uploadDevicePhoto
} from '../services/deviceImage.service';
import { TOAST_MESSAGES } from '../constants/global-constants';
import CustomInputBox from '../controls/CustomInputBox';
import CustomRadioButtonGroup from '../controls/CustomRadioButtonGroup';
import DeviceImageUpload, { PickedDeviceImage } from '../controls/DeviceImageUpload';

const options = [DeviceType.OUTDOOR, DeviceType.INDOOR];

type AddDeviceModalProps = {
  handleClose: (newDeviceAdded: boolean) => void;
  setIsGroupModalOpen: (state: boolean) => void;
  groups: Group[];
  actionData?: ActionData<DeviceAttributes>;
};
type FORMDATA = {
  deviceName: string;
  deviceType: DeviceType;
  serialNumber: string;
  purpose: string;
  type: string;
  latitude: string;
  longitude: string;
};

/**
 * Parses an optional coordinate field.
 * @param value - Raw input.
 * @returns The number, null when blank, or NaN when not a number.
 */
const parseCoordinate = (value: string | undefined): number | null => {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return null;
  return /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : NaN;
};

/**
 * Form values for a device, shared by the initial defaults and the reset on edit.
 * @param data - Device being edited, if any.
 * @returns Values for the form.
 */
const toFormValues = (data?: DeviceAttributes): Partial<FORMDATA> => ({
  deviceName: data?.deviceName || '',
  serialNumber: data?.serialNumber?.toString() || '',
  purpose: data?.purpose || '',
  deviceType: data?.deviceType || DeviceType.INDOOR,
  latitude: data?.latitude != null ? String(data.latitude) : '',
  longitude: data?.longitude != null ? String(data.longitude) : ''
});
/**
 * Modal - AddDeviceModal.
 * @param props - Properties of AddDeviceModal.
 * @returns ReactElement.
 */
export default function AddDeviceModal(props: AddDeviceModalProps): ReactElement {
  const { handleClose, setIsGroupModalOpen, groups, actionData } = props;
  const [device, setDevice] = useState<DeviceAttributes>({} as DeviceAttributes);
  const [coordinates, setCoordinates] = useState('(40.78, -74.18)');
  const [chips, setChips] = useState<string[]>([]);
  const inputTagRef = useRef<any>(null);
  const [deviceLocationMode, setDeviceLocationMode] = useState('coordinates');
  const [loading, setLoading] = useState(false);
  // Preview URL shown in the control. `pickedImage` holds the bytes only when
  // the user has chosen a new file this session; an unchanged existing photo
  // has a preview but nothing to re-upload.
  const [deviceImage, setDeviceImage] = useState<string | null>(null);
  const [pickedImage, setPickedImage] = useState<PickedDeviceImage | null>(null);

  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: useMemo(() => toFormValues(actionData?.data), [actionData])
  });

  const { handleSubmit, reset, getValues, setValue, trigger } = methods;
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    reset(toFormValues(actionData?.data));
  }, [actionData]);

  /**
   * Validation for one coordinate field: optional, but numeric, in range, and
   * only together with the other half.
   * @param label - Field name for messages.
   * @param limit - Largest allowed absolute value.
   * @param other - The paired field.
   * @returns A react-hook-form validate function.
   */
  const coordinateRule =
    (label: string, limit: number, other: 'latitude' | 'longitude') =>
    (value: string): true | string => {
      const parsed = parseCoordinate(value);
      const otherParsed = parseCoordinate(getValues(other));
      if (parsed === null) {
        return otherParsed === null ? true : `Enter a ${label.toLowerCase()} too, or clear both`;
      }
      if (Number.isNaN(parsed)) return `${label} must be a number`;
      if (Math.abs(parsed) > limit) return `${label} must be between -${limit} and ${limit}`;
      return true;
    };

  /** Fills latitude/longitude from the browser's location, for setting up on site. */
  const fillCurrentLocation = (): void => {
    if (!navigator.geolocation) {
      toast.error('Location is not available in this browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setValue('latitude', position.coords.latitude.toFixed(6), { shouldDirty: true });
        setValue('longitude', position.coords.longitude.toFixed(6), { shouldDirty: true });
        trigger(['latitude', 'longitude']);
        setLocating(false);
      },
      () => {
        toast.error('Could not get your location');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  /**
   * Sends the chosen image to the server so it is visible to everyone on the
   * account and to the mobile app, rather than living in this browser only.
   *
   * The device itself is already saved by the time this runs, so an image
   * failure warns instead of failing the whole save.
   * @param serialNumber - Device serial number.
   */
  const persistDeviceImage = async (serialNumber: number): Promise<void> => {
    if (Number.isNaN(serialNumber)) {
      return;
    }
    try {
      if (pickedImage?.blob) {
        await uploadDevicePhoto(serialNumber, pickedImage.blob);
        // Clear any legacy browser-local copy now that the server has it.
        removeDeviceImage(serialNumber);
      } else if (!deviceImage) {
        await deleteDevicePhoto(serialNumber);
        removeDeviceImage(serialNumber);
      }
    } catch {
      toast.warn('Device saved, but the image could not be uploaded.');
    }
  };

  /**
   * Function to create a new device.
   * @param formData - Form data.
   */
  const handleSaveDevice = async (formData: FORMDATA): Promise<any> => {
    const allChips = inputTagRef?.current?.forceAddChip();
    // if (formData.type === 'Outdoor') {
    //   allChips.push('Outdoor');
    // }
    try {
      setLoading(true);
      const serialNumber = parseInt(formData.serialNumber.toString(), 10);
      const data: AddDeviceV2Props = {
        id: device.id,
        serialNumber,
        purpose: formData.purpose,
        tags: allChips,
        deviceName: formData.deviceName,
        deviceType: formData.deviceType,
        latitude: parseCoordinate(formData.latitude),
        longitude: parseCoordinate(formData.longitude)
      } as AddDeviceV2Props;

      if (actionData?.type === 'edit') {
        await editDevice(data, device.id);
        await persistDeviceImage(serialNumber);
        toast.success(TOAST_MESSAGES.edited);
        handleClose(true);
        setLoading(false);
        return;
      }
      await addDeviceV2(data);
      await persistDeviceImage(serialNumber);
      toast.success(TOAST_MESSAGES.added);
      setLoading(false);
      handleClose(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (actionData?.type === 'edit') {
      setDevice(actionData.data);
      setChips(actionData.data?.tags || []);
      setPickedImage(null);
      setDeviceImage(null);
      resolveDeviceImage(actionData.data?.serialNumber).then(setDeviceImage);
    } else {
      setPickedImage(null);
      setDeviceImage(null);
    }
  }, [actionData]);

  return (
    <SidePanelContainer closeModal={() => handleClose(false)}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">
        {actionData?.type === 'edit' ? 'Edit Device' : 'Add Device'}
      </div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
        {actionData?.type === 'edit' ? '' : 'Add new devices to TelosAir'}
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSaveDevice)}>
          <CustomInputBox
            label="Device name"
            idname="deviceName"
            type="text"
            validationObject={{ required: 'Device name is required' }}
          />
          <CustomInputBox
            label="Device Number"
            idname="serialNumber"
            type="text"
            validationObject={{ required: 'Device number is required' }}
          />
          {/* <InputBox textValue={' '} setText={() => null} label="Gateway #" /> */}

          {/* Groups Dropdown */}
          {/* <Dropdown
        label="Group"
        name="group"
        options={groups.map((group) => ({ groupName: group.groupName }))}
        handleOptionsSelect={handleOptionsSelect}
        field="groupName"
      /> */}

          {/* CREATE GROUP BUTTON */}
          {/* <div className="my-8">
        <Button
          theme="secondary-gray"
          size="sm"
          name="Create Group"
          onClick={() => {
            setIsGroupModalOpen(true);
          }}
        />
      </div> */}

          {/* <Dropdown
        label="Configuration"
        name="config"
        options={configuration}
        handleOptionsSelect={handleOptionsSelect}
        field="configurationName"
      /> */}
          <CustomRadioButtonGroup<string>
            idName="deviceType"
            options={options.map((option) => ({
              id: option,
              name: option === 'OUTDOOR' ? 'Outdoor' : 'Indoor'
            }))}
            label="DeviceType"
          />
          <MultiSelectInputBox
            ref={inputTagRef}
            label="Tags"
            availableOptions={[]}
            chips={chips}
            setChips={setChips}
          />
          <CustomInputBox label="Purpose" idname="purpose" type="text" />

          <div className="mt-4 flex items-end justify-between">
            <div className="text-sm text-gray-500">
              Map location <span className="text-gray-400">(optional)</span>
            </div>
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-primary-500 hover:underline disabled:opacity-50"
              onClick={fillCurrentLocation}
              disabled={locating}
            >
              <span className="material-icons-outlined text-sm">my_location</span>
              {locating ? 'Locating…' : 'Use my current location'}
            </button>
          </div>
          <div className="flex gap-3">
            <CustomInputBox
              label="Latitude"
              idname="latitude"
              type="text"
              placeholder="e.g. 40.7812"
              validationObject={{
                validate: coordinateRule('Latitude', 90, 'longitude'),
                deps: ['longitude']
              }}
            />
            <CustomInputBox
              label="Longitude"
              idname="longitude"
              type="text"
              placeholder="e.g. -74.1801"
              validationObject={{
                validate: coordinateRule('Longitude', 180, 'latitude'),
                deps: ['latitude']
              }}
            />
          </div>

          <DeviceImageUpload
            value={deviceImage}
            onChange={(image) => {
              setPickedImage(image);
              setDeviceImage(image?.dataUrl ?? null);
            }}
          />

          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div>
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => handleClose(false)}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              loading={loading}
              name="Save Changes"
              buttonType="submit"
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
